import { useCallback, useEffect, useMemo, useState } from 'react'
import { EMPTY_COMPANY_FILTER, type BoxTruckCompanyFilterOptions } from '../components/BoxTruckCompanyFilter'
import {
    boxTruckCompanyFilterStorageKey,
    migrateBoxTruckCompanyFilter,
    readBoxTruckCompanyFilter,
    removeLegacyBoxTruckCompanyFilters,
    writeBoxTruckCompanyFilter,
} from '../utils/companyFilterStorage'

interface UseBoxTruckCompanyFilterParams {
    projectName: string | undefined
    userId: string | undefined
}

interface UseBoxTruckCompanyFilterResult {
    companyFilter: BoxTruckCompanyFilterOptions
    companyFilterReady: boolean
    setCompanyFilter: (next: BoxTruckCompanyFilterOptions) => void
}

/** One seven-day filter per user and company. In-app search tabs read the same value, while the browser
 * storage event keeps separate browser tabs/windows in sync. Load Hub owns a different key and behaviour. */
export const useBoxTruckCompanyFilter = ({ projectName, userId }: UseBoxTruckCompanyFilterParams): UseBoxTruckCompanyFilterResult => {
    const storageKey = boxTruckCompanyFilterStorageKey(projectName, userId)
    const persistedFilter = useMemo(() => readBoxTruckCompanyFilter(storageKey) ?? EMPTY_COMPANY_FILTER, [storageKey])
    const [filtersByStorageKey, setFiltersByStorageKey] = useState<Record<string, BoxTruckCompanyFilterOptions>>({})
    const companyFilter = storageKey ? (filtersByStorageKey[storageKey] ?? persistedFilter) : EMPTY_COMPANY_FILTER

    useEffect(() => {
        if (!storageKey) return
        removeLegacyBoxTruckCompanyFilters(projectName, userId)
        const next = migrateBoxTruckCompanyFilter(projectName, userId) ?? readBoxTruckCompanyFilter(storageKey) ?? EMPTY_COMPANY_FILTER
        setFiltersByStorageKey((current) => ({ ...current, [storageKey]: next }))
    }, [projectName, storageKey, userId])

    useEffect(() => {
        if (!storageKey) return
        const syncFromStorage = (event: StorageEvent) => {
            if (event.key !== null && event.key !== storageKey) return
            const next = readBoxTruckCompanyFilter(storageKey) ?? EMPTY_COMPANY_FILTER
            setFiltersByStorageKey((current) => ({ ...current, [storageKey]: next }))
        }
        window.addEventListener('storage', syncFromStorage)
        return () => window.removeEventListener('storage', syncFromStorage)
    }, [storageKey])

    const setCompanyFilter = useCallback(
        (next: BoxTruckCompanyFilterOptions) => {
            if (!storageKey) return
            setFiltersByStorageKey((current) => ({ ...current, [storageKey]: next }))
            writeBoxTruckCompanyFilter(storageKey, next)
        },
        [storageKey]
    )

    return { companyFilter, companyFilterReady: storageKey !== null, setCompanyFilter }
}
